import { useIsMutating } from '@tanstack/react-query'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatDate } from '@/lib/formatters'
import {
  ENDORSEMENT_STEP_KEY,
  EndorsementStaffAction,
  endorsementSteps,
  type InternalEndorsement,
  useEndorsementStep,
} from '@/models/internalEndorsement'
import { cn } from '@/utils/cn'
import { BASIS_LABELS, changeLines, KIND_LABELS } from '../../PolicyChanges/policyChangeCopy'
import { confirmStep } from './confirmStep'
import { CAI_UPDATES, corgiOutcomeText, needsPortal } from './endorsementCopy'
import { ReasonDialog } from './ReasonDialog'

/** Every change in the request with its date and why that date; one a carrier won't take can be dropped. */
export function EndorsementChangeLines({ endorsement }: { endorsement: InternalEndorsement }) {
  const drop = useEndorsementStep(endorsementSteps.dropChange)
  // A drop clears the prices, so it waits for any quote save, and the other steps, to land.
  const stepping = useIsMutating({ mutationKey: ENDORSEMENT_STEP_KEY }) > 0
  const canDrop = endorsement.actions.includes(EndorsementStaffAction.DROP)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Changes</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Fixed columns that wrap, so the table fits the card and each row's drop button stays in view; on a
            phone it keeps a readable width and scrolls sideways instead of squeezing the What column away. */}
        <Table className="min-w-[40rem] table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[24%]">Change</TableHead>
              <TableHead>What</TableHead>
              <TableHead className="w-28">Effective</TableHead>
              <TableHead className="w-40">Why that date</TableHead>
              {canDrop && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {endorsement.changes.map((line) => {
              const lines = changeLines(line)
              return (
                <TableRow key={line.id} className={cn(line.dropped && 'text-neutral-400')}>
                  <TableCell className="align-top font-medium whitespace-normal">
                    {KIND_LABELS[line.kind]}
                    <div className="mt-1 flex flex-col items-start gap-1 font-normal">
                      {line.dropped ? (
                        <Badge variant="warning">Dropped</Badge>
                      ) : (
                        <>
                          <Badge variant={CAI_UPDATES[line.caiUpdate].variant}>
                            {CAI_UPDATES[line.caiUpdate].label}
                          </Badge>
                          <span className="text-xs text-neutral-600">{CAI_UPDATES[line.caiUpdate].text}</span>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="align-top break-words whitespace-normal">
                    <div>{lines.length > 0 ? lines[0] : 'No details'}</div>
                    {lines.slice(1).map((text) => (
                      <div key={text} className="text-xs text-neutral-600">
                        {text}
                      </div>
                    ))}
                    {line.dropped && (
                      <div className="mt-1 text-xs text-neutral-600">
                        <span className="font-medium">Why dropped:</span> {line.dropReason}
                      </div>
                    )}
                    {!line.dropped &&
                      (line.corgi ?? []).map((outcome) => (
                        <div
                          key={`${outcome.policyNumber}-${outcome.outcome}`}
                          className={cn(
                            'mt-1 text-xs',
                            needsPortal(outcome) ? 'text-error' : 'text-neutral-600'
                          )}
                        >
                          {corgiOutcomeText(outcome)}
                        </div>
                      ))}
                  </TableCell>
                  <TableCell className="align-top">{formatDate(line.effectiveDate)}</TableCell>
                  <TableCell className="align-top whitespace-normal">
                    {line.effectiveBasis && BASIS_LABELS[line.effectiveBasis]}
                  </TableCell>
                  {canDrop && (
                    <TableCell className="text-right align-top">
                      {!line.dropped && (
                        <ReasonDialog
                          trigger="Drop change"
                          trashIcon
                          disabled={stepping}
                          title={`Drop ${KIND_LABELS[line.kind].toLowerCase()}`}
                          description="It comes off every policy, since the request is all or nothing. Prices entered so far are cleared."
                          label="Why can't it go ahead? The customer sees this."
                          confirmLabel="Drop change"
                          onConfirm={(reason) =>
                            confirmStep(
                              drop.mutateAsync({ id: endorsement.id, changeId: line.id, reason }),
                              'Dropped the change from every policy.'
                            )
                          }
                        />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
